import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import { showSuccess, showError } from "utils/swalHelper";
import { useFormik } from "formik";
import * as Yup from "yup";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { autocompleteDropdownStyles } from "Constants/Style";
import { toast } from "react-toastify";

interface Props {
  show: boolean;
  onClose: () => void;
  onCreated?: () => void;
  warehouseId?: string | number;
  editRow: any;
}

interface FormValues {
  warehouse: any;
  category: any;
  sub_category: any;
  item: any;
  item_code: string;
  unit_name: string;
  reorder_level: string;
}

const EditModal: React.FC<Props> = ({ show, onClose, onCreated, warehouseId, editRow }) => {
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const validationSchema = Yup.object({
    warehouse: Yup.object().nullable().required("Warehouse is required"),
    category: Yup.object().nullable().required("Category is required"),
    sub_category: Yup.object().nullable().required("Sub Category is required"),
    item: Yup.object().nullable().required("Item is required"),
    reorder_level: Yup.number()
      .typeError("Reorder level must be a number")
      .positive("Reorder level must be greater than 0")
      .required("Reorder level is required"),
  });
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      warehouse: editRow ? editRow?.warehouse : null,
      category: editRow ? editRow?.master_item?.category : null,
      sub_category: editRow ? editRow?.master_item?.sub_category : null,
      item: editRow ? editRow?.master_item : null,
      item_code: editRow ? editRow?.master_item?.item_code : "",
      unit_name: editRow ? editRow?.master_item?.unit?.unit_name : "",
      reorder_level: editRow ? String(editRow?.reorder_level) : "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!values.item) return showError("Please select an item", "Validation");
      if (!values.reorder_level) return showError("Please enter reorder level", "Validation");

      try {
        setSaving(true);
        const payload: any = {
          reorder_type: "warehouse",
          reorder_level: Number(values.reorder_level),
        };

        if(editRow?.reorder_level_id){
          await Axios.put(`${api.Inventory.Reorderlevel.WarehouseWise?.edit}/${editRow?.reorder_level_id}`, payload);
          await showSuccess("Reorder level updated successfully", "Updated");
          onCreated && onCreated();
          onClose();
        }else{
          showError("Invalid reorder level ID", "Error");
        }
      } catch (err: any) {
        console.error("Update reorder level failed", err);
        const serverMsg =
          err?.response?.data?.message || err?.response?.data?.error || err?.message;
        const friendly = serverMsg || "Failed to create reorder level";
        showError(friendly, "Error");
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (show) {
      fetchCategories();
      Axios.get(`${api?.master?.warehouse?.dropdown}`).then((result) => {
        setWarehouseOptions(result?.data?.data || [])
      }).catch((err) => {
        toast.error("Failed to fetch warehouse dropdown")
      });
    }
    if (!show) {
      formik.resetForm();
      setSubCategories([]);
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await Axios.get(`${api.master.category.dropdown}`);
      const resp = res?.data;
      let list: any[] = [];
      if (Array.isArray(resp)) list = resp;
      else if (Array.isArray(res?.data?.data)) list = res.data.data;
      else if (Array.isArray(res?.data?.payload)) list = res.data.payload;
      else if (resp && typeof resp === "object") list = Object.values(resp);
      // Normalize to objects with `id` and `name` so selects always send id
      const normalized = (list || []).map((it: any) => ({
        id: it?.id ?? it?.category_id ?? it?.value ?? it?.key ?? "",
        name:
          it?.name ?? it?.category_name ?? it?.label ?? it?.category_name ??
          (typeof it === "string" ? it : ""),
        raw: it,
      }));
      setCategories(normalized);
    } catch (err: any) {
      console.error("Failed to fetch categories", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async (catId: string | number) => {
    try {
      // setLoading(true);
      // endpoint: /master/sub-category/dropdown/category/{categoryId}
      const res = await Axios.get(
        `${api.master.sub_category.fetchSubCategoryDropdownByCategoryId}/${catId}`
      );
      const resp = res?.data;
      let list: any[] = [];
      if (Array.isArray(resp)) list = resp;
      else if (Array.isArray(res?.data?.data)) list = res.data.data;
      else if (Array.isArray(res?.data?.payload)) list = res.data.payload;
      else if (resp && typeof resp === "object") list = Object.values(resp);
      const normalized = (list || []).map((it: any) => ({
        id: it?.id ?? it?.sub_category_id ?? it?.value ?? it?.key ?? "",
        name:
          it?.name ?? it?.sub_category_name ?? it?.label ?? it?.sub_category_name ??
          (typeof it === "string" ? it : ""),
        raw: it,
      }));
      setSubCategories(normalized);
    } catch (err) {
      console.error("Failed to fetch subcategories", err);
      setSubCategories([]);
    } finally {
      // setLoading(false);
    }
  };

  const fetchItems = async (subCatId: string | number) => {
    try {
      // setLoading(true);
      // endpoint: /master/item/dropdown/sub-category/{subCategoryId}
      const res = await Axios.get(
        `${api.master.item.itemDropdownBySubcategoryId}/${subCatId}`
      );
      const resp = res?.data;
      let list: any[] = [];
      if (Array.isArray(resp)) list = resp;
      else if (Array.isArray(res?.data?.data)) list = res.data.data;
      else if (Array.isArray(res?.data?.payload)) list = res.data.payload;
      else if (resp && typeof resp === "object") list = Object.values(resp);
      const normalized = (list || []).map((it: any) => {
        // unit may be nested object like { unit_id, unit_name, ... }
        const unitObj = it?.unit || it?.unit_name || null;
        const unit_name =
          (unitObj && typeof unitObj === "object" && (unitObj.unit_name || unitObj.unitName)) ||
          it?.unit_name ||
          it?.unit?.unit_name ||
          it?.unit?.unitName ||
          "";

        return {
          id: it?.id ?? it?.item_id ?? it?.value ?? it?.key ?? "",
          item_name: it?.item_name ?? it?.name ?? it?.label ?? "",
          item_code: it?.item_code ?? it?.code ?? "",
          unit_name,
          raw: it,
        };
      });
      setItems(normalized);
    } catch (err) {
      console.error("Failed to fetch items", err);
      setItems([]);
    } finally {
      // setLoading(false);
    }
  };

  const onCategoryChange = (value: any) => {
    formik.setFieldValue("category", value);
    formik.setFieldValue("sub_category", null);
    formik.setFieldValue("item", null);
    formik.setFieldValue("item_code", "");
    formik.setFieldValue("unit_name", "");
    setSubCategories([]);
    setItems([]);
    if (value) {
      const catId = value?.id || value?.category_id || value?.value;
      if (catId) fetchSubCategories(catId);
    }
  };

  const onSubCategoryChange = (value: any) => {
    formik.setFieldValue("sub_category", value);
    formik.setFieldValue("item", null);
    formik.setFieldValue("item_code", "");
    formik.setFieldValue("unit_name", "");
    setItems([]);
    if (value) {
      const subCatId = value?.id || value?.sub_category_id || value?.value;
      if (subCatId) fetchItems(subCatId);
    }
  };

  const onItemChange = (value: any) => {
    formik.setFieldValue("item", value);
    if (value) {
      const itemCode = value?.item_code || value?.code || value?.raw?.item_code || "";
      const unitFromRaw = value?.raw?.unit;
      const unitVal =
        (unitFromRaw && (unitFromRaw.unit_name || unitFromRaw.unitName)) ||
        value?.unit_name ||
        "";
      formik.setFieldValue("item_code", itemCode);
      formik.setFieldValue("unit_name", unitVal);
    } else {
      formik.setFieldValue("item_code", "");
      formik.setFieldValue("unit_name", "");
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update Reorder Level</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="create-reorder-modal">
          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <Form onSubmit={formik.handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Warehouse<span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      size="small"
                      disabled
                      options={warehouseOptions || []}
                      autoHighlight
                      value={formik.values.warehouse}
                      onChange={(_, val: any) => { formik.setFieldValue('warehouse', val) }}
                      onBlur={() => formik.setFieldTouched("warehouse", true)}
                      getOptionLabel={(option: any) =>
                        option ? option?.name || option?.label || option?.warehouse_name || "" : ""
                      }
                      renderOption={(props, option: any) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt: any) => opt?.name || opt?.label || opt?.warehouse_name || ""
                        )
                      }
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          placeholder="Select Warehouse"
                          fullWidth
                          error={formik.touched.warehouse && Boolean(formik.errors.warehouse)}
                          helperText={
                            formik.touched.warehouse && typeof formik.errors.warehouse === "string"
                              ? formik.errors.warehouse
                              : undefined
                          }
                          sx={{
                            background: "#FFF",
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor:
                                  formik.touched.warehouse && formik.errors.warehouse
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  formik.touched.warehouse && formik.errors.warehouse
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  formik.touched.warehouse && formik.errors.warehouse
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Category<span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      disabled
                      size="small"
                      options={categories || []}
                      autoHighlight
                      value={formik.values.category}
                      onChange={(_, val) => onCategoryChange(val)}
                      onBlur={() => formik.setFieldTouched("category", true)}
                      getOptionLabel={(option: any) =>
                        option ? option?.name || option?.label || option?.warehouse || "" : ""
                      }
                      renderOption={(props, option: any) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt: any) => opt?.name || opt?.label || opt?.category_name || ""
                        )
                      }
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          placeholder="Select Category"
                          fullWidth
                          error={formik.touched.category && Boolean(formik.errors.category)}
                          helperText={
                            formik.touched.category && typeof formik.errors.category === "string"
                              ? formik.errors.category
                              : undefined
                          }
                          sx={{
                            background: "#FFF",
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor:
                                  formik.touched.category && formik.errors.category
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  formik.touched.category && formik.errors.category
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  formik.touched.category && formik.errors.category
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Sub Category<span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      disabled
                      size="small"
                      options={subCategories || []}
                      autoHighlight
                      value={formik.values.sub_category}
                      onChange={(_, val) => onSubCategoryChange(val)}
                      onBlur={() => formik.setFieldTouched("sub_category", true)}
                      getOptionLabel={(option: any) =>
                        option
                          ? option?.name || option?.label || option?.sub_category_name || ""
                          : ""
                      }
                      renderOption={(props, option: any) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt: any) =>
                            opt?.name || opt?.label || opt?.sub_category_name || ""
                        )
                      }
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          placeholder="Select Sub Category"
                          fullWidth
                          error={
                            formik.touched.sub_category && Boolean(formik.errors.sub_category)
                          }
                          helperText={
                            formik.touched.sub_category &&
                              typeof formik.errors.sub_category === "string"
                              ? formik.errors.sub_category
                              : undefined
                          }
                          sx={{
                            background: "#FFF",
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor:
                                  formik.touched.sub_category && formik.errors.sub_category
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  formik.touched.sub_category && formik.errors.sub_category
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  formik.touched.sub_category && formik.errors.sub_category
                                    ? "#dc3545"
                                    : "#ced4da",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Item<span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      disabled
                      size="small"
                      options={items || []}
                      autoHighlight
                      value={formik.values.item}
                      onChange={(_, val) => onItemChange(val)}
                      onBlur={() => formik.setFieldTouched("item", true)}
                      getOptionLabel={(option: any) =>
                        option ? option?.item_name || option?.label || option?.name || "" : ""
                      }
                      renderOption={(props, option: any) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt: any) => opt?.item_name || opt?.label || opt?.name || ""
                        )
                      }
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          placeholder="Select Item"
                          fullWidth
                          error={formik.touched.item && Boolean(formik.errors.item)}
                          helperText={
                            formik.touched.item && typeof formik.errors.item === "string"
                              ? formik.errors.item
                              : undefined
                          }
                          sx={{
                            background: "#FFF",
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor:
                                  formik.touched.item && formik.errors.item ? "#dc3545" : "#ced4da",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  formik.touched.item && formik.errors.item ? "#dc3545" : "#ced4da",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  formik.touched.item && formik.errors.item ? "#dc3545" : "#ced4da",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Item Code</Form.Label>
                    <BootstrapTextField
                      fullWidth
                      disabled
                      size="small"
                      value={formik.values.item_code}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#FFF",
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Unit</Form.Label>
                    <BootstrapTextField
                      fullWidth
                      disabled
                      size="small"
                      value={formik.values.unit_name}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#FFF",
                          "& fieldset": {
                            borderColor: "#FFF",
                          },
                        },
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Reorder Level<span className="text-danger">*</span>
                    </Form.Label>
                    <BootstrapTextField
                      fullWidth
                      size="small"
                      type="number"
                      name="reorder_level"
                      value={formik.values.reorder_level}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.reorder_level && Boolean(formik.errors.reorder_level)
                      }
                      helperText={
                        formik.touched.reorder_level &&
                          typeof formik.errors.reorder_level === "string"
                          ? formik.errors.reorder_level
                          : undefined
                      }
                      inputProps={{ min: 0 }}
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor:
                              formik.touched.reorder_level && formik.errors.reorder_level
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              formik.touched.reorder_level && formik.errors.reorder_level
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor:
                              formik.touched.reorder_level && formik.errors.reorder_level
                                ? "#dc3545"
                                : "#ced4da",
                          },
                        },
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex w-100 justify-content-center gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditModal;
